import React, { useEffect, useRef, useState } from 'react';
import SlidePanel from '../../shared/SlidePanel';
import toast from '../../shared/Toast';
import { hapticSuccess, hapticTick } from '../../../_utils/haptics';
import { dialable } from './api';
import { firstName, replyDraft } from './needs';

/**
 * A reply to someone Bailey took a message from, drafted for the owner to
 * check: the saved answer when Bailey has one, otherwise a space for theirs.
 * It goes from the owner's own email or messages app, so nothing is sent
 * from here; once it's gone, Done clears their messages.
 */
export default function ReplyPanel({ group: g, answers, salonName, onDone, onTeach, onClose }) {
    const sheet = useRef(null);
    const box = useRef(null);
    const after = useRef(null);
    const [draft] = useState(() => replyDraft({ group: g, answers, salonName }));
    const [text, setText] = useState(draft.text);
    // Set once their email or messages app has been opened with the words.
    const [sentBy, setSentBy] = useState('');

    // The cursor goes where the owner's answer belongs.
    useEffect(() => {
        const el = box.current;
        if (!el) return;
        el.focus();
        try { el.setSelectionRange(draft.gap, draft.gap); } catch (e) { /* not supported */ }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const closeThen = (fn) => { after.current = fn; if (sheet.current) sheet.current.handleClose(); else { onClose(); if (fn) fn(); } };
    const closed = () => { onClose(); const fn = after.current; after.current = null; if (fn) fn(); };

    const who = firstName(g.name);
    const subject = salonName ? `Your message to ${salonName}` : 'Your message';
    const mail = g.email ? `mailto:${g.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}` : '';
    // "?&body=" is read by both iPhone and Android.
    const sms = g.phone ? `sms:${dialable(g.phone)}?&body=${encodeURIComponent(text)}` : '';
    const ready = !!text.trim();

    const copy = async () => {
        hapticTick();
        try { await navigator.clipboard.writeText(text); toast.success('Copied. Paste it into your email or messages.'); } catch (e) { toast.error('I couldn’t copy that. Select the words and copy them instead.'); }
    };

    const footer = sentBy ? (
        <div className="fd-panel-footer">
            <button type="button" className="sp-btn-secondary" onClick={() => { hapticTick(); closeThen(null); }}>Not yet</button>
            <button type="button" className="sp-btn-primary" onClick={() => { hapticSuccess(); closeThen(() => onDone(g)); }}>Sent, mark done</button>
        </div>
    ) : (
        <div className="fd-panel-footer">
            <button type="button" className="sp-btn-secondary" disabled={!ready} onClick={copy}>Copy</button>
            {sms && <a className={`${mail ? 'sp-btn-secondary' : 'sp-btn-primary'}${ready ? '' : ' is-off'}`} href={ready ? sms : undefined} onClick={() => { hapticTick(); setSentBy('text'); }}>Text it</a>}
            {mail && <a className={`sp-btn-primary${ready ? '' : ' is-off'}`} href={ready ? mail : undefined} onClick={() => { hapticTick(); setSentBy('email'); }}>Email it</a>}
        </div>
    );

    return (
        <SlidePanel ref={sheet} title={who ? `Reply to ${who}` : 'Reply'} onClose={closed} width={480} className="bly-panel fd-form fd-reply" footer={footer}>
            <div className="sp-form">
                <p className="fd-panel-lead">To {[g.email, g.phone].filter(Boolean).join(' or ')}</p>
                {sentBy ? (
                    <p className="fd-reply-sent">
                        {sentBy === 'email' ? 'I opened it in your email.' : 'I opened it in your messages.'} Once it’s sent, mark it done and their {g.count === 1 ? 'message clears' : `${g.count} messages clear`}.
                    </p>
                ) : !draft.known ? (
                    <p className="fd-price-note">I don’t know the answer to this one yet, so I’ve left a space for yours.</p>
                ) : null}
                <div className="sp-field">
                    <label className="sp-label" htmlFor="fd-reply-text">Your reply</label>
                    <textarea id="fd-reply-text" ref={box} className="sp-textarea" rows={9} value={text} onChange={(e) => setText(e.target.value)} />
                    {draft.known
                        ? <p className="sp-hint">From your answer to “{draft.known.question}”.</p>
                        : onTeach && <p className="sp-hint">Want me to answer this myself next time? <button type="button" className="fd-inline-link" onClick={() => { hapticTick(); closeThen(() => onTeach(g)); }}>Add the answer</button></p>}
                </div>
                <div className="sp-field">
                    <span className="sp-label">What they asked</span>
                    <p className="fd-reply-asked">{g.latest.text}</p>
                </div>
            </div>
        </SlidePanel>
    );
}
